import React, { useState } from 'react';
import { X, Lightning, Sparkle, CheckCircle, Spinner, Copy, ArrowRight, TextAa, MicrophoneStage, Envelope, Megaphone } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import api from '@/utils/api';
import { getUser } from '@/utils/auth';

const ImproveHookModal = ({ isOpen, onClose, onSuccess, onAction, initialHook = '' }) => {
  const user = getUser();
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  
  const [formData, setFormData] = useState({
    original_hook: initialHook,
    content_type: 'post',
    tone: 'curiosity'
  });

  const contentTypes = [
    { id: 'post', label: 'Social Post', desc: 'Text posts, captions', icon: TextAa },
    { id: 'reel', label: 'Reel/Video', desc: 'Short-form video', icon: MicrophoneStage },
    { id: 'email', label: 'Email', desc: 'Subject lines, openers', icon: Envelope },
    { id: 'sales_page', label: 'Sales Page', desc: 'Headlines, headers', icon: Megaphone }
  ];

  const tones = [
    { id: 'curiosity', label: 'Curiosity', desc: 'Makes them need to know more', color: 'purple' },
    { id: 'bold', label: 'Bold', desc: 'Confident and assertive', color: 'red' },
    { id: 'friendly', label: 'Friendly', desc: 'Warm and approachable', color: 'green' },
    { id: 'authority', label: 'Authority', desc: 'Expert and credible', color: 'blue' },
    { id: 'emotional', label: 'Emotional', desc: 'Connects with feelings', color: 'pink' }
  ];

  const handleSubmit = async () => {
    if (!formData.original_hook.trim()) {
      toast.error('Please enter your hook to improve');
      return;
    }

    setStep('generating');
    setLoading(true);

    try {
      const response = await api.post('/content/improve-hook', formData);
      setResult(response.data);
      setStep('result');
      if (onSuccess) onSuccess();
      toast.success('Your improved hooks are ready!');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response?.data?.detail || 'Daily limit reached. Upgrade for unlimited access!');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to improve hook');
      }
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleClose = () => {
    setStep('input');
    setResult(null);
    setFormData({
      original_hook: '',
      content_type: 'post',
      tone: 'curiosity'
    });
    onClose();
  };

  const handleTryAnother = () => {
    setStep('input');
    setResult(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="improve-hook-modal">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-2xl paygbot-modal-shell bg-slate-900 rounded-2xl shadow-2xl border border-white/10"
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <Lightning size={20} className="text-white" weight="fill" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Improve My Hook</h2>
                <p className="text-sm text-slate-400">Turn weak hooks into scroll-stoppers</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="close-hook-modal"
            >
              <X size={18} className="text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Input Step */}
            {step === 'input' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Hook Input */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Paste your hook to improve *
                  </label>
                  <textarea
                    value={formData.original_hook}
                    onChange={(e) => setFormData({...formData, original_hook: e.target.value})}
                    data-testid="hook-input"
                    placeholder="e.g., Here are some tips for growing your business..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-yellow-500 focus:border-transparent resize-none"
                    rows={3}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Paste the first line or hook from your content that you want to make more engaging.
                  </p>
                </div>

                {/* Content Type */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Content Type
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {contentTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setFormData({...formData, content_type: type.id})}
                          data-testid={`content-type-${type.id}`}
                          className={`p-3 rounded-lg text-left transition-all ${
                            formData.content_type === type.id
                              ? 'bg-yellow-500/20 border-yellow-500/50 border'
                              : 'bg-white/5 border-white/10 border hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            <Icon size={16} className={formData.content_type === type.id ? 'text-yellow-400' : 'text-slate-400'} />
                            <span className={`text-sm font-medium ${formData.content_type === type.id ? 'text-yellow-400' : 'text-white'}`}>
                              {type.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{type.desc}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Tone */}
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Desired Tone
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {tones.map((tone) => (
                      <button
                        key={tone.id}
                        onClick={() => setFormData({...formData, tone: tone.id})}
                        data-testid={`tone-${tone.id}`}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          formData.tone === tone.id
                            ? `bg-${tone.color}-500/20 border-${tone.color}-500/50 border text-${tone.color}-400`
                            : 'bg-white/5 border-white/10 border text-white hover:bg-white/10'
                        } ${formData.tone === tone.id ? 'ring-2 ring-yellow-500/50' : ''}`}
                      >
                        {tone.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {tones.find(t => t.id === formData.tone)?.desc}
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={!formData.original_hook.trim()}
                  data-testid="improve-hook-submit"
                  className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:from-slate-600 disabled:to-slate-600 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                >
                  <Lightning size={20} weight="fill" />
                  <span>Improve My Hook</span>
                  <ArrowRight size={18} />
                </button>
              </motion.div>
            )}

            {/* Generating Step */}
            {step === 'generating' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-12"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-4 animate-pulse">
                  <Lightning size={32} className="text-white" weight="fill" />
                </div>
                <div className="flex items-center space-x-2 text-white">
                  <Spinner size={20} className="animate-spin" />
                  <span>Making your hook scroll-stopping...</span>
                </div>
                <p className="text-sm text-slate-400 mt-2">Analyzing patterns and crafting variations</p>
              </motion.div>
            )}

            {/* Result Step */}
            {step === 'result' && result && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-6"
              >
                {/* Success Message */}
                <div className="flex items-center space-x-3 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <CheckCircle size={24} className="text-green-400" weight="fill" />
                  <div>
                    <p className="text-white font-medium">Your hooks are ready!</p>
                    <p className="text-sm text-slate-400">Pick the one that fits your content best</p>
                  </div>
                </div>

                {/* Improved Hook (Primary) */}
                <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/30 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Sparkle size={18} className="text-yellow-400" weight="fill" />
                      <span className="text-sm font-medium text-yellow-400">Best Improved Hook</span>
                    </div>
                    <button
                      onClick={() => handleCopy(result.improved_hook, 'improved')}
                      data-testid="copy-improved-hook"
                      className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-all ${
                        copiedField === 'improved'
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-white/10 text-white hover:bg-white/20'
                      }`}
                    >
                      {copiedField === 'improved' ? (
                        <>
                          <CheckCircle size={14} />
                          <span>Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-white text-lg font-medium">{result.improved_hook}</p>
                </div>

                {/* Variations */}
                <div>
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Alternative Variations</h4>
                  <div className="space-y-3">
                    {result.variations.map((variation, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white/5 border border-white/10 rounded-xl p-4"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                            {variation.style}
                          </span>
                          <button
                            onClick={() => handleCopy(variation.hook, `variation-${index}`)}
                            data-testid={`copy-variation-${index}`}
                            className={`flex items-center space-x-1 px-2 py-1 rounded-lg text-xs transition-all ${
                              copiedField === `variation-${index}`
                                ? 'bg-green-500/20 text-green-400'
                                : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            {copiedField === `variation-${index}` ? (
                              <CheckCircle size={12} />
                            ) : (
                              <Copy size={12} />
                            )}
                          </button>
                        </div>
                        <p className="text-white">{variation.hook}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Why These Are Better */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <h4 className="text-sm font-medium text-blue-400 mb-2 flex items-center space-x-2">
                    <Sparkle size={16} weight="fill" />
                    <span>Why These Hooks Are Stronger</span>
                  </h4>
                  <p className="text-sm text-slate-300">{result.improvement_reason}</p>
                </div>

                {/* Original Hook (Comparison) */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Your Original Hook</span>
                  </div>
                  <p className="text-slate-400 text-sm line-through">{result.original_hook}</p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleTryAnother}
                    className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <Lightning size={18} />
                    <span>Improve Another Hook</span>
                  </button>
                  <button
                    onClick={handleClose}
                    className="flex-1 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-medium rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <CheckCircle size={18} weight="fill" />
                    <span>Done</span>
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ImproveHookModal;
